import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { useAuthStore } from '@/stores/auth';
import { normalizeReadingProgress } from '@/utils/readingProgress';
type AuthStoreReadable = { getState: () => { user: { id: number } | null } }
const getAuthUser = (): { id: number } | null => {
  const store = useAuthStore as unknown as AuthStoreReadable
  return typeof store.getState === 'function' ? (store.getState().user ?? null) : null
}
import { addBookmark as apiAddBookmark, deleteBookmark as apiDeleteBookmark, addNote as apiAddNote, deleteNote as apiDeleteNote, updateNote as apiUpdateNote } from '@/api/books';

interface ReaderSettings {
  fontSize?: number;
  lineHeight?: number;
  theme?: 'light' | 'dark' | 'sepia';
  fontFamily?: string;
  pdfScale?: number;
  pdfRotation?: number;
  pdfScrollMode?: 'vertical' | 'horizontal';
  layoutMode?: 'reflowable' | 'fixed';
  fitMode?: 'width' | 'height' | 'auto';
  spreadMode?: 'auto' | 'none' | 'both';
}

interface ReadingProgress {
  [bookId: string]: number; // 阅读进度百分比 (0-100)
}

interface ReaderState {
  // 阅读进度
  readingProgress: ReadingProgress;
  
  // 阅读器设置
  readerSettings: ReaderSettings;
  
  // 当前阅读的书籍
  currentBookId: string | null;
  
  // 阅读历史
  readingHistory: Array<{
    bookId: string;
    title: string;
    lastReadTime: number;
    progress: number;
  }>;
  
  // 书签
  bookmarks: Array<{
    id: string;
    remoteId?: string;
    bookId: string;
    page: number;
    cfi?: string;
    title: string;
    note?: string;
    createdAt: number;
  }>;
  
  // 笔记
  notes: Array<{
    id: string;
    remoteId?: string;
    bookId: string;
    page: number;
    cfi?: string;
    content: string;
    highlight?: string;
    createdAt: number;
    updatedAt: number;
  }>;
  
  // 方法
  updateReadingProgress: (bookId: string, progress: number) => void;
  updateReaderSettings: (settings: ReaderSettings) => void;
  setCurrentBook: (bookId: string | null) => void;
  addToReadingHistory: (bookId: string, title: string, progress: number) => void;
  addBookmark: (bookmark: Omit<ReaderState['bookmarks'][0], 'id' | 'createdAt'>) => void;
  removeBookmark: (bookmarkId: string) => void;
  addNote: (note: Omit<ReaderState['notes'][0], 'id' | 'createdAt' | 'updatedAt'>) => void;
  updateNote: (noteId: string, content: string) => void;
  removeNote: (noteId: string) => void;
  getBookmarksByBook: (bookId: string) => ReaderState['bookmarks'];
  getNotesByBook: (bookId: string) => ReaderState['notes'];
  clearReadingData: (bookId: string) => void;
  replaceBookmarksForBook: (bookId: string, bookmarks: ReaderState['bookmarks']) => void;
  replaceNotesForBook: (bookId: string, notes: ReaderState['notes']) => void;
}

export const useReaderStore = create<ReaderState>()(
  persist(
    (set, get) => ({
      readingProgress: {},
      readerSettings: {
        fontSize: 16,
        lineHeight: 1.5,
        theme: 'light',
        fontFamily: 'Georgia',
        pdfScale: 1.0,
        pdfRotation: 0,
        pdfScrollMode: 'vertical',
        layoutMode: 'reflowable',
        fitMode: 'auto',
        spreadMode: 'auto'
      },
      currentBookId: null,
      readingHistory: [],
      bookmarks: [],
      notes: [],

      updateReadingProgress: (bookId, progress) => {
        set((state) => ({
          readingProgress: {
            ...state.readingProgress,
            [bookId]: normalizeReadingProgress(progress)
          }
        }));
      },

      updateReaderSettings: (settings) => {
        set((state) => ({
          readerSettings: {
            ...state.readerSettings,
            ...settings
          }
        }));
      },

      setCurrentBook: (bookId) => {
        set({ currentBookId: bookId });
      },

      addToReadingHistory: (bookId, title, progress) => {
        set((state) => {
          const existingIndex = state.readingHistory.findIndex(item => item.bookId === bookId);
          const newHistory = [...state.readingHistory];
          
          if (existingIndex >= 0) {
            newHistory[existingIndex] = {
              ...newHistory[existingIndex],
              lastReadTime: Date.now(),
              progress
            };
          } else {
            newHistory.push({
              bookId,
              title,
              lastReadTime: Date.now(),
              progress
            });
          }
          
          // 按最后阅读时间排序，最多保留50条记录
          newHistory.sort((a, b) => b.lastReadTime - a.lastReadTime);
          
          return {
            readingHistory: newHistory.slice(0, 50)
          };
        });
      },

      addBookmark: async (bookmark) => {
        let remoteId: string | undefined
        try {
          const user = getAuthUser()
          if (user) {
            const res = await apiAddBookmark({ book_id: bookmark.bookId, user_id: String(user.id), page: bookmark.page, cfi: bookmark.cfi, title: bookmark.title, note: bookmark.note })
            remoteId = String(res?.data?.id || '') || undefined
          }
        } catch (e) { void e }
        set((state) => ({
          bookmarks: [
            ...state.bookmarks,
            {
              ...bookmark,
              id: `bookmark_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              createdAt: Date.now(),
              remoteId
            }
          ]
        }))
      },

      removeBookmark: async (bookmarkId) => {
        try {
          const state = get()
          const bm = state.bookmarks.find(b => b.id === bookmarkId)
          const user = getAuthUser()
          if (user && bm?.remoteId) {
            await apiDeleteBookmark(String(bm.remoteId))
          }
        } catch (e) { void e }
        set((state) => ({
          bookmarks: state.bookmarks.filter(bookmark => bookmark.id !== bookmarkId)
        }))
      },

      addNote: async (note) => {
        let remoteId: string | undefined
        try {
          const user = getAuthUser()
          if (user) {
            const res = await apiAddNote({ book_id: note.bookId, user_id: String(user.id), page: note.page, cfi: note.cfi, content: note.content, highlight: note.highlight })
            remoteId = String(res?.data?.id || '') || undefined
          }
        } catch (e) { void e }
        set((state) => ({
          notes: [
            ...state.notes,
            {
              ...note,
              id: `note_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
              createdAt: Date.now(),
              updatedAt: Date.now(),
              remoteId
            }
          ]
        }))
      },

      updateNote: async (noteId, content) => {
        try {
          const state = get()
          const n = state.notes.find(x => x.id === noteId)
          const user = getAuthUser()
          if (user && n?.remoteId) {
            await apiUpdateNote(String(n.remoteId), content)
          }
        } catch (e) { void e }
        set((state) => ({
          notes: state.notes.map(note => 
            note.id === noteId 
              ? { ...note, content, updatedAt: Date.now() }
              : note
          )
        }))
      },

      removeNote: async (noteId) => {
        try {
          const state = get()
          const n = state.notes.find(x => x.id === noteId)
          const user = getAuthUser()
          if (user && n?.remoteId) {
            await apiDeleteNote(String(n.remoteId))
          }
        } catch (e) { void e }
        set((state) => ({
          notes: state.notes.filter(note => note.id !== noteId)
        }))
      },

      getBookmarksByBook: (bookId) => {
        const state = get();
        return state.bookmarks.filter(bookmark => bookmark.bookId === bookId);
      },

      getNotesByBook: (bookId) => {
        const state = get();
        return state.notes.filter(note => note.bookId === bookId);
      },

      clearReadingData: (bookId) => {
        set((state) => ({
          readingProgress: {
            ...state.readingProgress,
            [bookId]: 0
          },
          readingHistory: state.readingHistory.filter(item => item.bookId !== bookId),
          bookmarks: state.bookmarks.filter(bookmark => bookmark.bookId !== bookId),
          notes: state.notes.filter(note => note.bookId !== bookId)
        }));
      }
      ,
      replaceBookmarksForBook: (bookId, bookmarks) => {
        set((state) => ({
          bookmarks: [
            ...state.bookmarks.filter(b => b.bookId !== bookId),
            ...bookmarks
          ]
        }))
      },
      replaceNotesForBook: (bookId, notes) => {
        set((state) => ({
          notes: [
            ...state.notes.filter(n => n.bookId !== bookId),
            ...notes
          ]
        }))
      }
    }),
    {
      name: 'reader-store',
      partialize: (state) => ({
        readingProgress: state.readingProgress,
        readerSettings: state.readerSettings,
        readingHistory: state.readingHistory,
        bookmarks: state.bookmarks,
        notes: state.notes
      })
    }
  )
);
